import React from 'react';
import { Button } from 'react-bootstrap';
import { FaEye, FaCheckCircle, FaArrowDown, FaStar, FaRegStar } from 'react-icons/fa';
import CategorySelect from './CategorySelect';
import { formatGainPct, gainClass } from '../utils/gainFormat';

const THEME_ICON_SIZE = '1.35rem';
const renderThemeIcon = (enabled) => (
  enabled
    ? <FaStar style={{ color: '#f5c518', fontSize: THEME_ICON_SIZE }} />
    : <FaRegStar style={{ color: '#b0b0b0', fontSize: THEME_ICON_SIZE }} />
);

// Renders one Readylist row exactly as it appears on the Readylist page.
// Shared by Readylist.js (the source page) and TickerSearch.js so both
// places stay in sync. `extraRowStyle` mirrors WatchlistRow's optional
// seasonal "owned holding" tint prop.
const ReadylistRow = ({
  item,
  index,
  selected,
  highlighted,
  removing,
  deleting,
  themeToggleLoading,
  extraRowStyle,
  onRowClick,
  onQuickBuy,
  onCategoryUpdate,
  onThemeToggle,
  onDowngrade,
  onRemove,
}) => {
  const themeAccounts = item.theme_accounts || [];
  const aggregatedThemeEnabled = typeof item.theme_enabled === 'boolean'
    ? item.theme_enabled
    : themeAccounts.some(account => account.theme_enabled);
  const hasSingleAccountToggle = themeAccounts.length === 1 && themeAccounts[0].account_id;

  return (
    <tr
      className={`watchlist-row${selected ? ' watchlist-row-selected' : ''}${highlighted ? ' watchlist-row-highlight' : ''}${removing ? ' watchlist-row-removing' : ''}${item.is_purchased ? ' readylist-owned-row' : ''}`}
      onClick={() => onRowClick(item.id)}
      style={{ cursor: 'pointer', ...extraRowStyle }}
    >
      <td className="text-center" style={{ width: '40px', fontSize: '0.85rem', color: '#888' }}>{index + 1}</td>
      <td style={{ width: '60px' }}>{item.ticker}</td>
      <td className="text-center" style={{ width: '50px' }} onClick={e => e.stopPropagation()}>
        <button
          className="btn btn-success btn-sm"
          style={{
            fontSize: '1.2em',
            fontWeight: 'bold',
            padding: '2px 8px',
            lineHeight: '1'
          }}
          onClick={() => onQuickBuy({ symbol: item.ticker, price: item.price })}
          title={`Quick buy ${item.ticker}`}
        >
          +
        </button>
      </td>
      <td className="text-center" style={{ width: '110px', fontSize: '0.75rem' }}>
        {item.latest_note_updated_at ? (() => {
          const noteDate = new Date(item.latest_note_updated_at);
          const today = new Date();
          const isToday = noteDate.toDateString() === today.toDateString();
          const timeStr = noteDate.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: false
          }).replace(',', '');

          return isToday ? (
            <span className="badge bg-success" style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>
              {timeStr}
            </span>
          ) : timeStr;
        })() : '-'}
      </td>
      <td className="text-end" style={{ width: '60px' }}>{item.price !== undefined && item.price !== null ? `$${parseFloat(item.price).toFixed(2)}` : '-'}</td>
      <td className={`text-end ${gainClass(item.gain, 'watchlist')}`} style={{ width: '60px' }}>{formatGainPct(item.gain_pct)}</td>
      <td className="text-center" style={{ width: '60px' }}>
        {item.is_purchased ? (
          <FaCheckCircle color="#28a745" size="1.2em" title="Owned" />
        ) : (
          <FaEye color="#007bff" size="1.2em" title="Ready" />
        )}
      </td>
      <td className="text-end" style={{ width: '172px' }}>
        <CategorySelect
          tickerId={item.id}
          currentCategory={item.category ? { category_id: item.category_id, name: item.category } : null}
          onCategoryUpdate={onCategoryUpdate}
          isReadylist={true}
        />
      </td>
      <td className="text-center" style={{ width: '80px' }}>
        {hasSingleAccountToggle ? (() => {
          const accountTheme = themeAccounts[0];
          return (
            <button
              type="button"
              className="btn btn-link p-0"
              style={{ textDecoration: 'none' }}
              onClick={(e) => {
                e.stopPropagation();
                onThemeToggle(item);
              }}
              disabled={themeToggleLoading}
              title={accountTheme.theme_enabled ? 'Theme enabled' : 'Theme disabled'}
              aria-label={`Toggle theme ${accountTheme.theme_enabled ? 'enabled' : 'disabled'}`}
            >
              {themeToggleLoading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                renderThemeIcon(accountTheme.theme_enabled)
              )}
            </button>
          );
        })() : (
          <span
            className="d-inline-flex align-items-center justify-content-center"
            title={
              themeAccounts.length === 0
                ? 'Themes are available once this ticker belongs to an account.'
                : 'Theme is configured per account. Manage it from the holdings table.'
            }
          >
            {renderThemeIcon(aggregatedThemeEnabled)}
          </span>
        )}
      </td>
      <td className="text-end align-middle" style={{ width: '130px' }} onClick={e => e.stopPropagation()}>
        <Button
          variant="outline-warning"
          size="sm"
          className="btn-sm me-1"
          style={{ padding: '2px 6px', fontSize: '0.75rem', border: '1px solid #ffc107', backgroundColor: 'transparent' }}
          onClick={() => onDowngrade(item.id)}
          disabled={deleting}
          title="Downgrade to Watchlist"
        >
          {deleting ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          ) : (
            <FaArrowDown size="0.8em" />
          )}
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          className="btn-sm"
          style={{ padding: '2px 6px', fontSize: '0.75rem', border: '1px solid #dee2e6', backgroundColor: 'transparent' }}
          onClick={() => onRemove(item.id)}
          disabled={deleting}
          title="Remove"
        >
          {deleting ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          ) : (
            <i className="bi bi-trash text-muted"></i>
          )}
        </Button>
      </td>
    </tr>
  );
};

ReadylistRow.defaultProps = {
  extraRowStyle: {},
};

export default ReadylistRow;
